import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardHeader, CardTitle, CardContent, CardFooter } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { Building2, Package, Clock, CheckCircle2, AlertCircle, Send, FileText, IndianRupee, ChevronDown, ChevronRight, ChevronUp, Eye, EyeOff, Plus, X } from 'lucide-react';
import { formatDate } from '@/utils/dateFormat';
import { parseDescription, ANNEXURE_SECTIONS } from '@/utils/descAnnexure';
import { extractBrandCandidates } from '@/utils/brandCandidates';
import { markdownHtmlProps } from '@/utils/mdRender';

const API = '/api';

export default function VendorQuoteSubmission() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [expandedCategories, setExpandedCategories] = useState({});
  const [expandedDescriptions, setExpandedDescriptions] = useState({});
  
  // Vendor info
  const [vendorName, setVendorName] = useState('');
  const [vendorEmail, setVendorEmail] = useState('');
  const [vendorPhone, setVendorPhone] = useState('');
  const [vendorCompany, setVendorCompany] = useState('');
  const [notes, setNotes] = useState('');
  
  // Item rates
  const [itemRates, setItemRates] = useState({});
  const [itemRemarks, setItemRemarks] = useState({});
  // iter-379: Vendor Brand Preference — per-item brand choice picked
  // from the annexure's Indian brand-equivalent list. Submitted along
  // with the vendor rate and carried into the awarded PO.
  const [itemBrands, setItemBrands] = useState({});
  
  // GST & Discount for main quote
  const [gstPercentage, setGstPercentage] = useState(0);
  const [discountPercentage, setDiscountPercentage] = useState(0);
  
  // Suggest Additional Line Items
  const [additionalItems, setAdditionalItems] = useState([]);
  const [showAddItem, setShowAddItem] = useState(false);
  const [categoryList, setCategoryList] = useState([]);
  const [addItemsForm, setAddItemsForm] = useState({ vendor_name: '', vendor_email: '', notes: '', gst_percentage: 0, discount_percentage: 0 });
  const [addItemsSubmitting, setAddItemsSubmitting] = useState(false);
  const [addItemsSuccess, setAddItemsSuccess] = useState(false);
  const [newItem, setNewItem] = useState({
    main_category_code: '', main_category_name: '',
    sub_category_code: '', sub_category_name: '',
    description: '', quantity: 1, unit: 'Sqft', rate: 0
  });
  
  const getSubCategories = (mainCode) => {
    const cat = categoryList.find(c => c.code === mainCode);
    return cat?.sub_categories || [];
  };

  const handleAddItem = () => {
    if (!newItem.description.trim() || !newItem.main_category_code) {
      toast.error('Please fill category and description');
      return;
    }
    const amount = round(newItem.quantity * newItem.rate);
    setAdditionalItems(prev => [...prev, { ...newItem, id: `ai_${Date.now()}`, amount }]);
    setNewItem(prev => ({ ...prev, description: '', quantity: 1, rate: 0 }));
  };

  const handleRemoveItem = (id) => {
    setAdditionalItems(prev => prev.filter(i => i.id !== id));
  };

  const handleSubmitAdditionalItems = async () => {
    if (!additionalItems.length) return;
    const vName = (addItemsForm.vendor_name || vendorName || '').trim();
    const vEmail = (addItemsForm.vendor_email || vendorEmail || '').trim();
    if (!vName) { toast.error('Please enter your name'); return; }
    setAddItemsSubmitting(true);
    try {
      const subtotal = additionalItems.reduce((s, i) => s + i.amount, 0);
      const gstAmt = round(subtotal * (addItemsForm.gst_percentage || 0) / 100);
      const discountAmt = round(subtotal * (addItemsForm.discount_percentage || 0) / 100);
      await axios.post(`${API}/public/vendor-quote/${token}/additional-items`, {
        vendor_name: vName,
        vendor_email: vEmail,
        items: additionalItems.map(ai => ({
          main_category_code: ai.main_category_code,
          main_category_name: ai.main_category_name,
          sub_category_code: ai.sub_category_code || '',
          sub_category_name: ai.sub_category_name || '',
          description: ai.description,
          quantity: ai.quantity,
          unit: ai.unit,
          rate: ai.rate,
        })),
        notes: addItemsForm.notes || '',
        gst_percentage: addItemsForm.gst_percentage || 0,
        discount_percentage: addItemsForm.discount_percentage || 0,
      });
      toast.success(`${additionalItems.length} additional items submitted for review!`);
      setAddItemsSuccess(true);
      setAdditionalItems([]);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit additional items');
    } finally {
      setAddItemsSubmitting(false);
    }
  };

  const round = (n) => Math.round(n * 100) / 100;

  // Toggle description expand
  const toggleDescription = (itemId) => {
    setExpandedDescriptions(prev => ({
      ...prev,
      [itemId]: !prev[itemId]
    }));
  };

  useEffect(() => {
    fetchQuoteData();
    fetchCategories();
  }, [token]);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/public/vendor-quote/${token}/categories`);
      setCategoryList(response.data.categories || []);
    } catch (err) {
      console.error('Failed to fetch categories:', err);
    }
  };

  const fetchQuoteData = async () => {
    try {
      const response = await axios.get(`${API}/public/vendor-quote/${token}`);
      setData(response.data);
      
      // Pre-fill vendor info if available
      if (response.data.vendor_info) {
        setVendorName(response.data.vendor_info.name || '');
        setVendorEmail(response.data.vendor_info.email || '');
      }
      
      // Initialize all categories as expanded
      const expanded = {};
      response.data.grouped_items?.grouped?.forEach(group => {
        expanded[group.main_category?.code] = true;
      });
      setExpandedCategories(expanded);
      
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load quote data');
    } finally {
      setLoading(false);
    }
  };

  const toggleCategory = (groupCode) => {
    setExpandedCategories(prev => ({
      ...prev,
      [groupCode]: !prev[groupCode]
    }));
  };

  const updateRate = (itemId, value) => {
    setItemRates(prev => ({
      ...prev,
      [itemId]: parseFloat(value) || 0
    }));
  };

  const updateRemarks = (itemId, value) => {
    setItemRemarks(prev => ({
      ...prev,
      [itemId]: value
    }));
  };

  const calculateTotal = () => {
    let total = 0;
    data?.grouped_items?.grouped?.forEach(group => {
      group.sub_categories?.forEach(subCat => {
        subCat.items?.forEach(item => {
          const rate = itemRates[item.id] || 0;
          const quantity = item.quantity || 1;
          total += rate * quantity;
        });
      });
    });
    return total;
  };

  const countFilledItems = () => {
    return Object.values(itemRates).filter(rate => rate > 0).length;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!vendorName.trim()) {
      toast.error('Please enter your name');
      return;
    }
    
    if (!vendorEmail.trim() || !vendorEmail.includes('@')) {
      toast.error('Please enter a valid email');
      return;
    }
    
    if (!vendorCompany.trim()) {
      toast.error('Please enter your company name');
      return;
    }
    
    const filledItems = Object.entries(itemRates).filter(([_, rate]) => rate > 0);
    if (filledItems.length === 0) {
      toast.error('Please enter rates for at least one item');
      return;
    }
    
    setSubmitting(true);
    
    try {
      const items = filledItems.map(([itemId, rate]) => ({
        item_id: itemId,
        vendor_rate: rate,
        remarks: itemRemarks[itemId] || null,
        // iter-379: chosen brand (may be empty when the annexure has no
        // brand suggestions for this line or the vendor left it blank).
        selected_brand: (itemBrands[itemId] || '').trim() || null,
      }));
      
      const response = await axios.post(`${API}/public/vendor-quote/${token}/submit`, {
        vendor_name: vendorName.trim(),
        vendor_email: vendorEmail.trim(),
        vendor_phone: vendorPhone.trim() || null,
        vendor_company: vendorCompany.trim() || null,
        items,
        notes: notes.trim() || null,
        gst_percentage: gstPercentage || 0,
        discount_percentage: discountPercentage || 0,
        additional_items: additionalItems.filter(ai => ai.description.trim() && ai.rate > 0).map(ai => ({
          title: ai.description.trim(),
          description: '',
          main_category_code: ai.main_category_code || '',
          main_category_name: ai.main_category_name || '',
          sub_category_code: ai.sub_category_code || '',
          sub_category_name: ai.sub_category_name || '',
          quantity: ai.quantity,
          unit: ai.unit,
          rate: ai.rate,
          remarks: '',
        })),
      });
      
      toast.success('Quote submitted successfully!');
      
      // Refresh to show submitted state
      fetchQuoteData();
      
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit quote');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full text-center">
          <CardContent className="pt-8 pb-8">
            <AlertCircle className="mx-auto h-16 w-16 text-red-500 mb-4" />
            <h2 className="text-xl font-bold text-stone-900 mb-2">Quote Link Invalid</h2>
            <p className="text-stone-600">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (data?.expired) {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full text-center">
          <CardContent className="pt-8 pb-8">
            <Clock className="mx-auto h-16 w-16 text-amber-500 mb-4" />
            <h2 className="text-xl font-bold text-stone-900 mb-2">{data.message}</h2>
            <p className="text-stone-600">Project: {data.project_name}</p>
            <p className="text-sm text-stone-500 mt-2">
              Expired on: {formatDate(new Date(data.expired_at), { longMonth: true })}
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (data?.already_submitted) {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full text-center">
          <CardContent className="pt-8 pb-8">
            <CheckCircle2 className="mx-auto h-16 w-16 text-green-500 mb-4" />
            <h2 className="text-xl font-bold text-stone-900 mb-2">Quote Already Submitted</h2>
            <p className="text-stone-600">{data.message}</p>
            <p className="text-stone-600 mt-2">Project: {data.project_name}</p>
            {data.submitted_at && (
              <p className="text-sm text-stone-500 mt-2">
                Submitted on: {new Date(data.submitted_at).toLocaleString('en-IN', { dateStyle: 'long', timeStyle: 'short' })}
              </p>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-50">
      {/* Header */}
      <header className="bg-white border-b border-stone-200 sticky top-0 z-10">
        <div className="w-full px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {data?.company_info?.logo_url ? (
                <img src={data.company_info.logo_url} alt="Logo" className="h-10 w-auto" />
              ) : (
                <div className="h-10 w-10 bg-stone-100 rounded-lg flex items-center justify-center">
                  <Building2 className="text-stone-700" size={24} />
                </div>
              )}
              <div>
                <h1 className="font-bold text-stone-900">{data?.company_info?.name || 'Quote Submission'}</h1>
                <p className="text-sm text-stone-500">Vendor Quote Request</p>
              </div>
            </div>
            <Badge variant="outline" className="text-stone-600 border-stone-300">
              <Clock size={14} className="mr-1" />
              Expires: {data?.expiry_date ? formatDate(new Date(data.expiry_date)) : 'N/A'}
            </Badge>
          </div>
        </div>
      </header>

      <main className="w-full px-4 sm:px-6 lg:px-8 py-6">
        {/* Project Info */}
        <Card className="mb-6">
          <CardHeader className="pb-3">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-stone-100 rounded-lg">
                <FileText className="text-stone-700" size={20} />
              </div>
              <div>
                <CardTitle className="text-lg">{data?.project_name}</CardTitle>
                <p className="text-sm text-stone-500">{data?.project_type} Project • {data?.total_items} items</p>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="p-3 bg-stone-50 rounded-lg border border-stone-200">
              <p className="text-sm text-stone-700">
                <strong>Instructions:</strong> {data?.instructions}
              </p>
            </div>
          </CardContent>
        </Card>

        {/* iter-376: Project documents shared with the vendor. Only docs
            tagged `visible_to_vendor=true` reach this list (backend enforces
            the filter). Mirrors the layout used on the Public BOQ view. */}
        {data?.project_documents?.length > 0 && (
          <Card className="mb-6" data-testid="vendor-quote-project-documents">
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <FileText size={18} /> Project Documents
              </CardTitle>
              <p className="text-sm text-stone-500">
                Reference drawings and specifications shared for accurate pricing. Click to open in a new tab.
              </p>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {data.project_documents.map((d) => (
                  <a
                    key={d.id}
                    href={d.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="border border-stone-200 rounded-md p-3 hover:shadow-md hover:border-amber-300 transition-all bg-white group"
                    data-testid={`vendor-quote-doc-${d.id}`}
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <FileText size={16} className="text-stone-500 group-hover:text-amber-700" />
                      <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-stone-100 text-stone-600">
                        {(d.doc_type || 'other').replace('_', ' ')}
                      </span>
                    </div>
                    <div className="text-sm font-medium text-stone-900 line-clamp-1" title={d.name}>{d.name}</div>
                    <div className="text-xs text-blue-600 mt-1 opacity-0 group-hover:opacity-100 transition-opacity">Open →</div>
                  </a>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* iter-378: Annexure moved BELOW Scope Items + Additional Notes.
            See just after the Additional Notes card in the form section. */}

        <form onSubmit={handleSubmit}>
          {/* Vendor Details */}
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Building2 size={18} /> Your Details
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="vendorName">Your Name *</Label>
                  <Input
                    id="vendorName"
                    value={vendorName}
                    onChange={(e) => setVendorName(e.target.value)}
                    placeholder="Enter your name"
                    required
                    data-testid="vendor-name-input"
                  />
                </div>
                <div>
                  <Label htmlFor="vendorEmail">Email *</Label>
                  <Input
                    id="vendorEmail"
                    type="email"
                    value={vendorEmail}
                    onChange={(e) => setVendorEmail(e.target.value)}
                    placeholder="your@email.com"
                    required
                    data-testid="vendor-email-input"
                  />
                </div>
                <div>
                  <Label htmlFor="vendorPhone">Phone</Label>
                  <Input
                    id="vendorPhone"
                    value={vendorPhone}
                    onChange={(e) => setVendorPhone(e.target.value)}
                    placeholder="+91 XXXXX XXXXX"
                    data-testid="vendor-phone-input"
                  />
                </div>
                <div>
                  <Label htmlFor="vendorCompany">Company Name *</Label>
                  <Input
                    id="vendorCompany"
                    value={vendorCompany}
                    onChange={(e) => setVendorCompany(e.target.value)}
                    placeholder="Your company name"
                    required
                    data-testid="vendor-company-input"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Scope Items */}
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Package size={18} /> Scope Items
              </CardTitle>
              <p className="text-sm text-stone-500">Enter your rates for each item. Leave empty for items you're not quoting.</p>
            </CardHeader>
            <CardContent className="space-y-4">
              {data?.grouped_items?.grouped?.map((group) => (
                <div key={group.main_category?.code || 'unknown'} className="border border-stone-200 rounded-lg overflow-hidden">
                  {/* Main Category Header */}
                  <button
                    type="button"
                    onClick={() => toggleCategory(group.main_category?.code)}
                    className="w-full flex items-center justify-between p-3 bg-stone-50 hover:bg-stone-100 transition-colors"
                  >
                    <div className="flex items-center gap-2">
                      {expandedCategories[group.main_category?.code] ? (
                        <ChevronDown size={18} className="text-stone-500" />
                      ) : (
                        <ChevronRight size={18} className="text-stone-500" />
                      )}
                      <Badge variant="outline" className="text-stone-700">{group.main_category?.code}</Badge>
                      <span className="font-medium text-stone-900">{group.main_category?.name}</span>
                    </div>
                    <Badge variant="secondary">
                      {group.sub_categories?.reduce((acc, sc) => acc + (sc.items?.length || 0), 0) || 0} items
                    </Badge>
                  </button>
                  
                  {/* Sub-Categories and Items */}
                  {expandedCategories[group.main_category?.code] && (
                    <div className="divide-y divide-stone-100">
                      {group.sub_categories?.map((subCat) => (
                        <div key={subCat.sub_category?.code || 'uncategorized'}>
                          {/* Sub-Category Header */}
                          {subCat.sub_category && (
                            <div className="px-4 py-2 bg-stone-100 border-b border-stone-200">
                              <span className="text-sm font-medium text-stone-700">
                                {subCat.sub_category.code} - {subCat.sub_category.name}
                              </span>
                            </div>
                          )}
                          
                          {/* Items */}
                          {subCat.items?.map((item, idx) => (
                            <div key={item.id || idx} className="p-4 hover:bg-stone-50">
                              <div className="flex items-start justify-between gap-4">
                                <div className="flex-1 min-w-0">
                                  <p className="font-medium text-stone-900">{item.title || item.task_title}</p>
                                  {item.description && (
                                    <div className="mt-1">
                                      <p className={`text-sm text-stone-500 whitespace-pre-wrap ${!expandedDescriptions[item.id] ? 'line-clamp-2' : ''}`}>
                                        {item.description}
                                      </p>
                                      {item.description.length > 100 && (
                                        <button
                                          type="button"
                                          onClick={() => toggleDescription(item.id)}
                                          className="text-xs text-blue-600 hover:text-blue-800 mt-1 flex items-center gap-1"
                                        >
                                          {expandedDescriptions[item.id] ? (
                                            <>
                                              <EyeOff size={12} /> View Less
                                            </>
                                          ) : (
                                            <>
                                              <Eye size={12} /> View More
                                            </>
                                          )}
                                        </button>
                                      )}
                                    </div>
                                  )}
                                  <div className="flex items-center gap-3 mt-2">
                                    <Badge variant="outline" className="text-xs">
                                      Qty: {item.quantity || 1} {item.unit || 'No.s'}
                                    </Badge>
                                    {item.category_code && (
                                      <Badge variant="outline" className="text-xs bg-blue-50 text-blue-700">
                                        {item.category_code}
                                      </Badge>
                                    )}
                                  </div>
                                </div>
                                <div className="flex flex-col items-end gap-2 min-w-[200px]">
                                  <div className="flex items-center gap-2">
                                    <Label className="text-xs text-stone-500">Rate/Unit (₹)</Label>
                                    <Input
                                      type="number"
                                      min="0"
                                      step="0.01"
                                      value={itemRates[item.id] || ''}
                                      onChange={(e) => updateRate(item.id, e.target.value)}
                                      placeholder="0.00"
                                      className="w-28 text-right"
                                      data-testid={`rate-input-${item.id}`}
                                    />
                                  </div>
                                  {itemRates[item.id] > 0 && (
                                    <p className="text-sm font-medium text-green-600">
                                      = ₹{((itemRates[item.id] || 0) * (item.quantity || 1)).toLocaleString('en-IN')}
                                    </p>
                                  )}
                                  <Input
                                    value={itemRemarks[item.id] || ''}
                                    onChange={(e) => updateRemarks(item.id, e.target.value)}
                                    placeholder="Remarks (optional)"
                                    className="w-full text-sm"
                                    data-testid={`remarks-input-${item.id}`}
                                  />
                                  {/* iter-379: Vendor Brand Preference — extracted from the
                                      item's annexure description. Shown only when the
                                      description yields at least one Indian brand candidate. */}
                                  {(() => {
                                    const brands = extractBrandCandidates(item.description || '');
                                    if (!brands.length) return null;
                                    const chosen = itemBrands[item.id] || '';
                                    return (
                                      <div className="w-full mt-1" data-testid={`brand-picker-${item.id}`}>
                                        <Label className="text-xs text-stone-500 flex items-center gap-1 mb-1">
                                          Brand you will supply
                                          {chosen && (
                                            <span className="text-[10px] px-1 rounded bg-emerald-50 text-emerald-700 border border-emerald-200">
                                              {chosen}
                                            </span>
                                          )}
                                        </Label>
                                        <select
                                          value={chosen}
                                          onChange={(e) => setItemBrands((prev) => ({ ...prev, [item.id]: e.target.value }))}
                                          className="w-full text-sm border border-stone-300 rounded px-2 py-1.5 bg-white focus:outline-none focus:ring-1 focus:ring-amber-400"
                                          data-testid={`brand-select-${item.id}`}
                                        >
                                          <option value="">— Pick a brand —</option>
                                          {brands.map((b) => (
                                            <option key={b} value={b}>{b}</option>
                                          ))}
                                          <option value="__other__">Other (specify in remarks)</option>
                                        </select>
                                        {chosen === '__other__' && (
                                          <Input
                                            value={itemBrands[`${item.id}__custom`] || ''}
                                            onChange={(e) => setItemBrands((prev) => ({
                                              ...prev,
                                              [`${item.id}__custom`]: e.target.value,
                                              [item.id]: e.target.value || '__other__',
                                            }))}
                                            placeholder="Enter brand name"
                                            className="w-full text-sm mt-1"
                                            data-testid={`brand-custom-input-${item.id}`}
                                          />
                                        )}
                                      </div>
                                    );
                                  })()}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </CardContent>
          </Card>

          {/* Additional Notes */}
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="text-base">Additional Notes</CardTitle>
            </CardHeader>
            <CardContent>
              <Textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Any additional terms, conditions, or notes..."
                rows={3}
                data-testid="vendor-notes-input"
              />
            </CardContent>
          </Card>

          {/* iter-378: Annexure — moved BELOW Scope Items and Additional
              Notes so vendors first review pricing + notes, then cross-check
              specifications. Rendered only when the BOQ admin opts in via
              "Show on Vendor link" on Edit BOQ. Data sourced from each
              scope task's 6-section description. */}
          {data?.annexure_show_to_vendor && (() => {
            const flatItems = [];
            data?.grouped_items?.grouped?.forEach((group) => {
              group.sub_categories?.forEach((subCat) => {
                subCat.items?.forEach((it) => {
                  if (!it?.description || it.description.trim().length < 8) return;
                  flatItems.push({
                    ...it,
                    main_category_name: group.main_category?.name || group.main_category?.code || '',
                  });
                });
              });
            });
            if (!flatItems.length) return null;
            return (
              <Card className="mb-6" data-testid="vendor-quote-annexure">
                <CardHeader>
                  <CardTitle className="text-base flex items-center gap-2">
                    <FileText size={18} /> Annexure — Material, Brand & Installation Specifications
                  </CardTitle>
                  <p className="text-sm text-stone-500">
                    Reference specifications for the scope above. Confirm all brands, Indian Standards and installation notes before submitting.
                  </p>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto border border-stone-200 rounded-md">
                    <table className="min-w-full text-xs">
                      <thead className="bg-stone-50">
                        <tr>
                          <th className="px-2 py-2 text-left font-semibold text-stone-700 border-b border-stone-200 w-8">#</th>
                          <th className="px-2 py-2 text-left font-semibold text-stone-700 border-b border-stone-200 w-56">Line Item</th>
                          {ANNEXURE_SECTIONS.map((s) => (
                            <th key={s.key} className="px-2 py-2 text-left font-semibold text-stone-700 border-b border-stone-200 border-l">
                              {s.title}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {flatItems.map((it, i) => {
                          const parsed = parseDescription(it.description || '');
                          return (
                            <tr key={it.id || i} className="border-b border-stone-100 align-top" data-testid={`vendor-quote-annexure-row-${i}`}>
                              <td className="px-2 py-2 text-stone-500 text-center">{i + 1}</td>
                              <td className="px-2 py-2">
                                <div className="font-medium text-stone-900 line-clamp-2">{it.title || it.task_title || '—'}</div>
                                <div className="text-[10px] text-stone-500 mt-0.5">{it.main_category_name || 'Uncategorised'}</div>
                              </td>
                              {ANNEXURE_SECTIONS.map(({ key }) => (
                                <td
                                  key={key}
                                  className="px-2 py-2 border-l border-stone-100 max-w-[220px] text-stone-700 whitespace-pre-wrap"
                                  dangerouslySetInnerHTML={markdownHtmlProps(parsed[key] || '—')}
                                />
                              ))}
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                  <p className="text-[10px] text-stone-400 mt-1">
                    Highlighted keywords indicate materials, Indian brand-equivalents and IS codes to verify at execution and billing.
                  </p>
                </CardContent>
              </Card>
            );
          })()}

          {/* Suggest Additional Line Items - Matching screenshot design */}
          <div className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-xl shadow-lg p-6 mt-2 border-2 border-amber-200" data-testid="vendor-additional-items-section">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold text-xl text-amber-800 flex items-center gap-2">
                <Plus size={24} className="text-amber-600" />
                Suggest Additional Line Items
              </h3>
              <button
                type="button"
                onClick={() => setShowAddItem(!showAddItem)}
                className={`px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2 transition-all ${
                  showAddItem ? 'bg-amber-600 text-white' : 'bg-white text-amber-700 border border-amber-300 hover:bg-amber-50'
                }`}
                data-testid="toggle-add-items"
              >
                {showAddItem ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                {showAddItem ? 'Collapse' : 'Add Items'}
              </button>
            </div>

            {addItemsSuccess ? (
              <div className="bg-green-50 border border-green-200 rounded-lg p-6 text-center">
                <CheckCircle2 size={48} className="text-green-600 mx-auto mb-3" />
                <h4 className="font-semibold text-green-800 mb-2">Additional Items Submitted!</h4>
                <p className="text-sm text-green-700">Your suggestions have been sent to the team for review.</p>
                <button type="button" onClick={() => { setAddItemsSuccess(false); setShowAddItem(true); }} className="mt-3 text-sm text-green-600 hover:text-green-800 underline">Submit more items</button>
              </div>
            ) : showAddItem && (
              <div className="space-y-4">
                <p className="text-amber-700 text-sm">
                  Add items that were missed in the published scope. Select category to help organize items properly.
                </p>

                {/* Vendor Info */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-amber-800 mb-1">Your Name *</label>
                    <input
                      type="text"
                      value={addItemsForm.vendor_name || vendorName}
                      onChange={(e) => setAddItemsForm(prev => ({ ...prev, vendor_name: e.target.value }))}
                      placeholder="Your name or company"
                      className="w-full px-4 py-2.5 border border-amber-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent bg-white"
                      data-testid="add-items-vendor-name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-amber-800 mb-1">Email</label>
                    <input
                      type="email"
                      value={addItemsForm.vendor_email || vendorEmail}
                      onChange={(e) => setAddItemsForm(prev => ({ ...prev, vendor_email: e.target.value }))}
                      placeholder="your@email.com"
                      className="w-full px-4 py-2.5 border border-amber-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent bg-white"
                      data-testid="add-items-vendor-email"
                    />
                  </div>
                </div>

                {/* New Item Details */}
                <div className="bg-white rounded-lg p-4 border border-amber-200 space-y-3">
                  <p className="text-sm font-semibold text-stone-700">New Item Details</p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-stone-600 mb-1">Main Category *</label>
                      <select
                        value={newItem.main_category_code}
                        onChange={(e) => {
                          const cat = categoryList.find(c => c.code === e.target.value);
                          setNewItem(prev => ({ ...prev, main_category_code: e.target.value, main_category_name: cat?.name || '', sub_category_code: '', sub_category_name: '' }));
                        }}
                        className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm focus:ring-2 focus:ring-amber-500"
                        data-testid="add-item-main-category"
                      >
                        <option value="">Select Category</option>
                        {categoryList.map(cat => <option key={cat.code} value={cat.code}>{cat.code} - {cat.name}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-stone-600 mb-1">Sub-Category</label>
                      <select
                        value={newItem.sub_category_code}
                        onChange={(e) => {
                          const subs = getSubCategories(newItem.main_category_code);
                          const sub = subs.find(s => s.code === e.target.value);
                          setNewItem(prev => ({ ...prev, sub_category_code: e.target.value, sub_category_name: sub?.name || '' }));
                        }}
                        className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm focus:ring-2 focus:ring-amber-500"
                        disabled={!newItem.main_category_code}
                        data-testid="add-item-sub-category"
                      >
                        <option value="">Select Sub-Category</option>
                        {getSubCategories(newItem.main_category_code).map(sub => <option key={sub.code} value={sub.code}>{sub.code} - {sub.name}</option>)}
                      </select>
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-stone-600 mb-1">Task Title / Description *</label>
                    <input
                      type="text"
                      value={newItem.description}
                      onChange={(e) => setNewItem(prev => ({ ...prev, description: e.target.value }))}
                      placeholder="Task title / scope of work"
                      className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm focus:ring-2 focus:ring-amber-500"
                      data-testid="add-item-description"
                    />
                  </div>
                  <div className="grid grid-cols-3 md:grid-cols-4 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-stone-600 mb-1">Qty</label>
                      <input
                        type="number" step="0.01" min="0"
                        value={newItem.quantity}
                        onChange={(e) => setNewItem(prev => ({ ...prev, quantity: parseFloat(e.target.value) || 0 }))}
                        className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm focus:ring-2 focus:ring-amber-500"
                        data-testid="add-item-quantity"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-stone-600 mb-1">Unit</label>
                      <select
                        value={newItem.unit}
                        onChange={(e) => setNewItem(prev => ({ ...prev, unit: e.target.value }))}
                        className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm focus:ring-2 focus:ring-amber-500"
                        data-testid="add-item-unit"
                      >
                        {['Sqft', 'Rft', 'Cft', 'Nos', 'Kg', 'Ltr', 'Bag', 'Set', 'Lot', 'LS'].map(u => (
                          <option key={u} value={u}>{u}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-stone-600 mb-1">Rate (₹)</label>
                      <input
                        type="number" step="0.01" min="0"
                        value={newItem.rate || ''}
                        onChange={(e) => setNewItem(prev => ({ ...prev, rate: parseFloat(e.target.value) || 0 }))}
                        className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm focus:ring-2 focus:ring-amber-500"
                        data-testid="add-item-rate"
                      />
                    </div>
                    <div className="flex items-end">
                      <button
                        type="button"
                        onClick={handleAddItem}
                        disabled={!newItem.description.trim() || !newItem.main_category_code}
                        className="w-full px-4 py-2 bg-amber-600 text-white font-medium rounded-lg hover:bg-amber-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors text-sm flex items-center justify-center gap-1"
                        data-testid="add-item-btn"
                      >
                        <Plus size={14} /> Add
                      </button>
                    </div>
                  </div>
                </div>

                {/* Items Table */}
                {additionalItems.length > 0 && (
                  <div className="bg-white rounded-lg border border-amber-200 overflow-hidden">
                    <table className="w-full text-sm">
                      <thead className="bg-amber-50">
                        <tr>
                          <th className="text-left px-3 py-2 font-medium text-amber-800">Category</th>
                          <th className="text-left px-3 py-2 font-medium text-amber-800">Description</th>
                          <th className="text-right px-3 py-2 font-medium text-amber-800">Qty</th>
                          <th className="text-left px-3 py-2 font-medium text-amber-800">Unit</th>
                          <th className="text-right px-3 py-2 font-medium text-amber-800">Rate</th>
                          <th className="text-right px-3 py-2 font-medium text-amber-800">Amount</th>
                          <th className="px-2 py-2"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {additionalItems.map((item) => (
                          <tr key={item.id} className="border-t border-stone-100">
                            <td className="px-3 py-2 text-xs text-stone-600">{item.main_category_code}{item.sub_category_code ? ` / ${item.sub_category_code}` : ''}</td>
                            <td className="px-3 py-2">{item.description}</td>
                            <td className="px-3 py-2 text-right">{item.quantity}</td>
                            <td className="px-3 py-2">{item.unit}</td>
                            <td className="px-3 py-2 text-right">₹{item.rate.toLocaleString('en-IN')}</td>
                            <td className="px-3 py-2 text-right font-medium">₹{item.amount.toLocaleString('en-IN')}</td>
                            <td className="px-2 py-2">
                              <button type="button" onClick={() => handleRemoveItem(item.id)} className="text-red-400 hover:text-red-600"><X size={14} /></button>
                            </td>
                          </tr>
                        ))}
                        <tr className="border-t-2 border-amber-300 bg-amber-50">
                          <td colSpan={5} className="px-3 py-2 font-semibold text-amber-800 text-right">Total:</td>
                          <td className="px-3 py-2 font-bold text-amber-900 text-right">
                            ₹{additionalItems.reduce((s, i) => s + i.amount, 0).toLocaleString('en-IN')}
                          </td>
                          <td></td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                )}

                {/* GST & Discount Row */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-amber-800 mb-1">GST %</label>
                    <select
                      value={addItemsForm.gst_percentage}
                      onChange={(e) => setAddItemsForm(prev => ({ ...prev, gst_percentage: parseFloat(e.target.value) || 0 }))}
                      className="w-full px-4 py-2.5 border border-amber-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent bg-white"
                      data-testid="add-items-gst"
                    >
                      <option value={0}>0% (No GST)</option>
                      <option value={5}>5%</option>
                      <option value={12}>12%</option>
                      <option value={18}>18%</option>
                      <option value={28}>28%</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-amber-800 mb-1">Discount %</label>
                    <input
                      type="number" min="0" max="100" step="0.5"
                      value={addItemsForm.discount_percentage || ''}
                      onChange={(e) => setAddItemsForm(prev => ({ ...prev, discount_percentage: parseFloat(e.target.value) || 0 }))}
                      placeholder="0"
                      className="w-full px-4 py-2.5 border border-amber-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent bg-white"
                      data-testid="add-items-discount"
                    />
                  </div>
                </div>

                {/* Totals summary */}
                {additionalItems.length > 0 && (
                  <div className="bg-white rounded-lg p-3 border border-amber-200 text-sm space-y-1">
                    <div className="flex justify-between"><span className="text-stone-600">Subtotal:</span><span className="font-medium">₹{additionalItems.reduce((s, i) => s + i.amount, 0).toLocaleString('en-IN')}</span></div>
                    {addItemsForm.gst_percentage > 0 && (
                      <div className="flex justify-between"><span className="text-stone-600">GST ({addItemsForm.gst_percentage}%):</span><span className="font-medium">₹{round(additionalItems.reduce((s, i) => s + i.amount, 0) * addItemsForm.gst_percentage / 100).toLocaleString('en-IN')}</span></div>
                    )}
                    {addItemsForm.discount_percentage > 0 && (
                      <div className="flex justify-between text-red-600"><span>Discount ({addItemsForm.discount_percentage}%):</span><span className="font-medium">-₹{round(additionalItems.reduce((s, i) => s + i.amount, 0) * addItemsForm.discount_percentage / 100).toLocaleString('en-IN')}</span></div>
                    )}
                    <div className="flex justify-between border-t border-amber-200 pt-1"><span className="font-semibold text-amber-800">Grand Total:</span><span className="font-bold text-amber-900">₹{(() => {
                      const sub = additionalItems.reduce((s, i) => s + i.amount, 0);
                      const gst = round(sub * (addItemsForm.gst_percentage || 0) / 100);
                      const disc = round(sub * (addItemsForm.discount_percentage || 0) / 100);
                      return round(sub + gst - disc).toLocaleString('en-IN');
                    })()}</span></div>
                  </div>
                )}

                {/* Notes */}
                <div>
                  <label className="block text-sm font-medium text-amber-800 mb-1">Notes (Optional)</label>
                  <textarea
                    rows={2}
                    value={addItemsForm.notes}
                    onChange={(e) => setAddItemsForm(prev => ({ ...prev, notes: e.target.value }))}
                    placeholder="Any additional context about these items..."
                    className="w-full px-4 py-2.5 border border-amber-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent resize-none bg-white"
                    data-testid="add-items-notes"
                  />
                </div>

                <div className="flex justify-center pt-2">
                  <button
                    type="button"
                    disabled={!additionalItems.length || addItemsSubmitting}
                    onClick={handleSubmitAdditionalItems}
                    className="inline-flex items-center gap-2 px-8 py-3 bg-amber-600 text-white font-bold rounded-lg hover:bg-amber-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-lg hover:shadow-xl"
                    data-testid="submit-additional-items-btn"
                  >
                    <Send size={16} />
                    {addItemsSubmitting ? 'Submitting...' : `Submit ${additionalItems.length} Additional Item${additionalItems.length !== 1 ? 's' : ''} for Review`}
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Summary & Submit */}
          <Card className="border-stone-300 bg-stone-50">
            <CardContent className="pt-6 space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-stone-600">Items Quoted</p>
                  <p className="text-2xl font-bold text-stone-900">{countFilledItems()} / {data?.total_items}</p>
                </div>
                <div className="text-right">
                  <p className="text-sm text-stone-600">Subtotal</p>
                  <p className="text-xl font-bold text-stone-800 flex items-center justify-end gap-1">
                    <IndianRupee size={18} />
                    {calculateTotal().toLocaleString('en-IN')}
                  </p>
                </div>
              </div>

              {/* GST & Discount Section */}
              <div className="bg-white p-4 rounded-lg border border-stone-200 space-y-3">
                <p className="text-sm font-semibold text-stone-700">Taxes & Discounts</p>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs text-stone-500">GST %</Label>
                    <select
                      value={gstPercentage}
                      onChange={(e) => setGstPercentage(parseFloat(e.target.value) || 0)}
                      className="w-full border rounded-md px-3 py-2 text-sm mt-1 focus:ring-2 focus:ring-stone-400"
                      data-testid="main-gst-select"
                    >
                      <option value={0}>0% (No GST)</option>
                      <option value={5}>5%</option>
                      <option value={12}>12%</option>
                      <option value={18}>18%</option>
                      <option value={28}>28%</option>
                    </select>
                  </div>
                  <div>
                    <Label className="text-xs text-stone-500">Discount %</Label>
                    <Input
                      type="number"
                      min="0"
                      max="100"
                      step="0.5"
                      value={discountPercentage || ''}
                      onChange={(e) => setDiscountPercentage(parseFloat(e.target.value) || 0)}
                      placeholder="0"
                      className="mt-1"
                      data-testid="main-discount-input"
                    />
                  </div>
                </div>

                {/* Totals Breakdown */}
                <div className="border-t border-stone-200 pt-3 space-y-1.5">
                  <div className="flex justify-between text-sm">
                    <span className="text-stone-600">Subtotal:</span>
                    <span className="font-medium">₹{calculateTotal().toLocaleString('en-IN')}</span>
                  </div>
                  {gstPercentage > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-stone-600">GST ({gstPercentage}%):</span>
                      <span className="font-medium text-blue-700">+₹{round(calculateTotal() * gstPercentage / 100).toLocaleString('en-IN')}</span>
                    </div>
                  )}
                  {discountPercentage > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-stone-600">Discount ({discountPercentage}%):</span>
                      <span className="font-medium text-red-600">-₹{round(calculateTotal() * discountPercentage / 100).toLocaleString('en-IN')}</span>
                    </div>
                  )}
                  <div className="flex justify-between text-base font-bold pt-2 border-t border-stone-200">
                    <span>Grand Total:</span>
                    <span className="text-stone-900 flex items-center gap-1">
                      <IndianRupee size={16} />
                      {(() => {
                        const sub = calculateTotal();
                        const gst = round(sub * gstPercentage / 100);
                        const disc = round(sub * discountPercentage / 100);
                        return round(sub + gst - disc).toLocaleString('en-IN');
                      })()}
                    </span>
                  </div>
                </div>
              </div>

              <Button
                type="submit"
                disabled={submitting || countFilledItems() === 0}
                className="w-full bg-stone-900 hover:bg-stone-800"
                size="lg"
                data-testid="submit-quote-btn"
              >
                {submitting ? (
                  <span className="flex items-center gap-2">
                    <span className="animate-spin">&#8987;</span> Submitting...
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    <Send size={18} /> Submit Quote
                  </span>
                )}
              </Button>
            </CardContent>
          </Card>
        </form>
      </main>

      {/* Footer */}
      <footer className="bg-white border-t border-stone-200 mt-12 py-6">
        <div className="w-full px-4 sm:px-6 lg:px-8 text-center text-sm text-stone-500">
          <p>Powered by {data?.company_info?.name || 'Bella Casa'}</p>
          {data?.company_info?.phone && (
            <p className="mt-1">Contact: {data.company_info.phone}</p>
          )}
        </div>
      </footer>
    </div>
  );
}
